import { useRef, useState } from "react";
import { UploadCloud, FileCheck2, X, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/enrollment/api";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic";
const MAX_BYTES = 10 * 1024 * 1024;

function humanSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentUploader({ email, docType, label, helper, data, set, required = true }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(0);

  const filename = data[`doc_${docType}_filename`];
  const size = data[`doc_${docType}_size`];
  const uploadedAt = data[`doc_${docType}_uploaded_at`];
  const uploaded = Boolean(data[`doc_${docType}_id`]);

  const handleFiles = async (files) => {
    if (!files || !files[0]) return;
    const file = files[0];
    if (file.size > MAX_BYTES) {
      toast.error("File exceeds the 10 MB limit.");
      return;
    }
    setUploading(true);
    setProgress(10);
    try {
      const form = new FormData();
      form.append("file", file);
      const url = `${API}/enrollment/${encodeURIComponent(email)}/documents/${docType}`;
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.min(95, Math.round((e.loaded / e.total) * 100));
          setProgress(pct);
        }
      };
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (e) { reject(e); }
          } else {
            try {
              const err = JSON.parse(xhr.responseText);
              reject(new Error(err.detail || `Upload failed (${xhr.status})`));
            } catch {
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(form);
      });
      setProgress(100);
      // Mirror onto enrollment data so the UI updates immediately
      set(`doc_${docType}_id`, result.id);
      set(`doc_${docType}_filename`, result.original_filename);
      set(`doc_${docType}_size`, result.size);
      set(`doc_${docType}_uploaded_at`, result.created_at);
      toast.success(`${label} uploaded.`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 600);
    }
  };

  const handleRemove = async () => {
    try {
      const url = `${API}/enrollment/${encodeURIComponent(email)}/documents/${docType}`;
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      set(`doc_${docType}_id`, null);
      set(`doc_${docType}_filename`, null);
      set(`doc_${docType}_size`, null);
      set(`doc_${docType}_uploaded_at`, null);
      toast.message(`${label} removed.`);
    } catch (e) {
      toast.error("Could not remove file");
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploading) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      className="border-2 border-dashed border-[#E5E0D8] rounded-sm bg-white/70 p-5 hover:border-[#1B3B2B]/40 transition-colors"
      data-testid={`uploader-${docType}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-eyebrow text-[#1A1A1A]">
            {label}
            {required && <span className="text-[#CD7F32] ml-1">*</span>}
          </div>
          {helper && <p className="text-xs text-[#4B5563] mt-1.5 max-w-md">{helper}</p>}
        </div>
        {uploaded && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1B3B2B] text-white text-[10px] uppercase tracking-widest rounded-sm flex-shrink-0">
            <FileCheck2 size={11} /> Uploaded
          </span>
        )}
      </div>

      {uploaded ? (
        <div className="mt-4 p-3 bg-[#F4F1EA] border border-[#E5E0D8] rounded-sm flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileCheck2 size={20} className="text-[#1B3B2B] flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-sm text-[#1A1A1A] truncate" title={filename}>{filename}</div>
              <div className="text-xs text-[#4B5563]">
                {humanSize(size)}{uploadedAt ? ` · ${new Date(uploadedAt).toLocaleString()}` : ""}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            data-testid={`uploader-${docType}-remove`}
            className="flex-shrink-0 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#991B1B] hover:text-[#7F1D1D] font-semibold"
          >
            <X size={14} /> Replace
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={[
            "mt-4 p-6 border-2 border-dashed rounded-sm cursor-pointer transition-colors text-center",
            dragging ? "border-[#1B3B2B] bg-[#1B3B2B]/5" : "border-[#E5E0D8] hover:border-[#1B3B2B]/60",
            uploading ? "opacity-60 cursor-wait" : "",
          ].join(" ")}
          data-testid={`uploader-${docType}-dropzone`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="animate-spin text-[#1B3B2B]" size={24} />
              <div className="text-sm text-[#4B5563]">Uploading… {progress}%</div>
              <div className="w-48 h-1 bg-[#E5E0D8] rounded-sm overflow-hidden">
                <div
                  className="h-full bg-[#CD7F32] transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <UploadCloud size={28} className="text-[#1B3B2B]" />
              <div className="text-sm text-[#1A1A1A]">
                <span className="font-semibold">Click to upload</span> or drag-and-drop
              </div>
              <div className="text-xs text-[#4B5563]">PDF, JPG, PNG, or HEIC · max 10 MB</div>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            data-testid={`uploader-${docType}-input`}
          />
        </div>
      )}
    </div>
  );
}

export function PrivacyNotice() {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-sm p-6">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-sm bg-[#1B3B2B] text-white flex items-center justify-center">
          <ShieldCheck size={20} />
        </div>
        <div>
          <div className="label-eyebrow text-[#CD7F32]">Privacy & Security · Required Reading</div>
          <h3 className="font-serif-display text-2xl text-[#1B3B2B] mt-1">
            Your student's records are protected.
          </h3>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5 text-sm text-[#1A1A1A] leading-relaxed">
        <div>
          <div className="label-eyebrow">How we store</div>
          <p className="mt-2">
            Documents are uploaded over an encrypted (TLS) connection and stored in our
            private object-storage system. They are <strong>never publicly accessible</strong> —
            only authorized PRSVR administrators with the admin password can view them.
          </p>
        </div>
        <div>
          <div className="label-eyebrow">How we don't use them</div>
          <p className="mt-2">
            We do <strong>not</strong> sell, share, or trade your records with any third party,
            advertiser, data broker, or partner, ever. We use them solely to verify enrollment,
            coordinate transcripts, and meet California school-of-record requirements.
          </p>
        </div>
        <div>
          <div className="label-eyebrow">California compliance</div>
          <p className="mt-2">
            Under the California Consumer Privacy Act (CCPA / CPRA) and the Student Online
            Personal Information Protection Act (SOPIPA, Cal. Bus. & Prof. Code §§ 22584–22585),
            you have the right to (a) know what personal information we hold, (b) request
            deletion, and (c) opt out of any sale or sharing — though, as noted above, PRSVR
            does not sell or share student data. We comply with FERPA-aligned safeguards
            for educational records.
          </p>
        </div>
        <div>
          <div className="label-eyebrow">Retention & deletion</div>
          <p className="mt-2">
            We retain records only as long as your student is enrolled, plus a five-year
            archival window required for transcript reissuance, after which records are
            destroyed. You may request earlier deletion in writing at{" "}
            <strong>records@prsvr.school</strong>.
          </p>
        </div>
      </div>
      <div className="mt-5 flex items-start gap-2 text-xs text-[#4B5563]">
        <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
        <span>
          Only the parent or legal guardian completing this enrollment should upload documents.
          If you do not have a document yet, you may proceed and email it later to{" "}
          <strong>records@prsvr.school</strong>.
        </span>
      </div>
    </div>
  );
}
